import { ResultadoReconciliacao } from '../api/sga-reconciliacao/sgaReconciliacaoApi';
import { DivergenciaSga } from '../api/sga-divergencia/sgaDivergenciaApi';
import { reexibirDivergencias } from './divergenciaDispensada';

/**
 * A regra de "o que dizer depois de clicar em Verificar no SGA" (at-76).
 *
 * Extraída de `views/adesoes` quando a ação passou a existir também em
 * `/ti/envios` — convenção da at-24: regra repetida em duas telas é divergência
 * com data marcada. As telas cuidam do estado e do toast; a decisão mora aqui.
 *
 * 🔴 Os quatro desfechos são DIFERENTES entre si e precisam continuar sendo
 * distinguíveis (mesma lição da at-53 na tela de oficina e da at-66 nos estados
 * vazios): "não deu para conferir" não é "está tudo certo", e nenhum dos dois é
 * "achei o veículo no SGA e vinculei".
 */
export type SituacaoVerificacaoSga =
  /** SGA fora do ar, carteira de outro consultor, registro sem contraparte lá. */
  | 'indisponivel'
  /** Conferido e idêntico. */
  | 'sem-diferencas'
  /** Conferido e há divergências a mostrar. */
  | 'com-diferencas'
  /**
   * at-78: o veículo pendente foi **achado no SGA e vinculado** — o quarto desfecho
   * que o comentário acima já anunciava e que não existia.
   */
  | 'veiculo-vinculado'
  /**
   * at-78 (B23): o associado está conferido, mas **algum veículo pendente não foi
   * encontrado** no SGA. É o caso que produzia o relato de 15/09/2026 — a tela
   * dizia "nenhuma diferença" com o veículo seguindo pendente.
   */
  | 'veiculo-pendente';

export interface LeituraVerificacaoSga {
  situacao: SituacaoVerificacaoSga;
  /**
   * Texto pronto para o toast. Nunca vazio.
   *
   * ⚠️ **Quem decide se mostra é a tela**, e em `com-diferencas` isso importa:
   * onde a faixa `AvisoDivergenciaSga` é renderizada, ela já conta a história, e
   * um toast junto seria a mesma informação duas vezes. `/adesoes` só toasta
   * quando `reexibidas > 0` — o caso em que a faixa sozinha não explicaria por que
   * algo dispensado reapareceu.
   */
  mensagem: string;
  divergencias: DivergenciaSga[];
  /**
   * Quantas divergências o usuário havia dispensado e voltaram a aparecer.
   * Só > 0 em `com-diferencas`.
   */
  reexibidas: number;
}

const SEM_RESPOSTA = 'Não foi possível conferir no SGA agora. O cadastro local segue disponível.';

/**
 * ⚠️ **Tem efeito colateral de propósito:** em `com-diferencas` ela desfaz a
 * dispensa das divergências encontradas (at-54 — "quem pede para ver, vê"). O "×"
 * da faixa continua valendo para o aviso PASSIVO, que aparece sozinho; aqui
 * alguém clicou em um botão pedindo a conferência.
 *
 * Sem isso o clique fica MUDO quando tudo já foi dispensado: este ramo não emite
 * toast próprio (quem fala é a faixa), e a faixa se recusa a renderizar o que foi
 * dispensado — resultado indistinguível de "está tudo certo" e de "o botão não
 * funcionou".
 */
export function interpretarVerificacaoSga(
  resultado: ResultadoReconciliacao,
): LeituraVerificacaoSga {
  if (!resultado?.verificado) {
    return {
      situacao: 'indisponivel',
      mensagem: resultado?.motivo?.trim() || SEM_RESPOSTA,
      divergencias: [],
      reexibidas: 0,
    };
  }

  const divergencias = resultado.divergencias ?? [];
  const veiculos = resultado.veiculosPendentes ?? [];

  /*
   * at-78 (B23): o desfecho dos VEÍCULOS vem ANTES do "nenhuma diferença".
   *
   * 🔴 O botão do associado também tenta vincular os veículos pendentes (at-76).
   * Anunciar "nenhuma diferença em relação ao SGA" enquanto um veículo continua
   * pendente — e o próprio botão acabou de falhar em resolvê-lo — é dizer que está
   * tudo certo quando não está. Foi o relato de Victor em 15/09/2026.
   *
   * ⚠️ Vinculado tem precedência sobre não encontrado: é a notícia acionável (a
   * lista muda), e o não encontrado continua visível como pendência na própria tela.
   */
  const vinculados = veiculos.filter((v) => v.situacao === 'VINCULADO');
  if (vinculados.length > 0) {
    return {
      situacao: 'veiculo-vinculado',
      mensagem:
        vinculados.length === 1
          ? vinculados[0].mensagem
          : `${vinculados.length} veículos foram encontrados no SGA e vinculados.`,
      divergencias,
      reexibidas: 0,
    };
  }

  const naoResolvidos = veiculos.filter(
    (v) => v.situacao === 'NAO_ENCONTRADO' || v.situacao === 'DONO_DIFERENTE',
  );
  if (naoResolvidos.length > 0 && divergencias.length === 0) {
    return {
      situacao: 'veiculo-pendente',
      mensagem:
        naoResolvidos.length === 1
          ? `O cadastro do associado está conferido. ${naoResolvidos[0].mensagem}`
          : `O cadastro do associado está conferido, mas ${naoResolvidos.length} veículos seguem pendentes no SGA.`,
      divergencias: [],
      reexibidas: 0,
    };
  }

  if (divergencias.length === 0) {
    return {
      situacao: 'sem-diferencas',
      mensagem: 'Cadastro conferido: nenhuma diferença em relação ao SGA.',
      divergencias: [],
      reexibidas: 0,
    };
  }

  const reexibidas = reexibirDivergencias(divergencias.map((d) => d.id));

  return {
    situacao: 'com-diferencas',
    mensagem:
      reexibidas === 1
        ? 'Conferido: 1 diferença que havia sido dispensada voltou a ser exibida.'
        : reexibidas > 1
          ? `Conferido: ${reexibidas} diferenças que haviam sido dispensadas voltaram a ser exibidas.`
          : divergencias.length === 1
            ? 'Conferido: 1 diferença encontrada em relação ao SGA.'
            : `Conferido: ${divergencias.length} diferenças encontradas em relação ao SGA.`,
    divergencias,
    reexibidas,
  };
}
